import React, { createContext, useContext, useMemo } from 'react';
import type { CharacterData } from '~/types/storage';
import type { TagAction } from '~/pages/CharacterSheet/types';
import { computeAllDerived, type DerivedCharacterValues } from '~/utils/characterDerived';

export interface CharacterActions {
  onRoll: (name: string, pool: number, flatHits?: number) => void;
  onTagAction: (action: TagAction) => void;
}

export interface CharacterContextValue {
  characterData: CharacterData;
  derived: DerivedCharacterValues;
  actions: CharacterActions;
}

const CharacterContext = createContext<CharacterContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useCharacterContext = (): CharacterContextValue | null => {
  return useContext(CharacterContext);
};

interface CharacterContextProviderProps {
  characterData: CharacterData;
  actions: CharacterActions;
  children: React.ReactNode;
}

export const CharacterContextProvider: React.FC<CharacterContextProviderProps> = ({
  characterData,
  actions,
  children,
}) => {
  const derived = useMemo(() => computeAllDerived(characterData), [characterData]);

  const value = useMemo(() => ({
    characterData,
    derived,
    actions,
  }), [characterData, derived, actions]);

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};

// Re-export types for consumers
export type { DerivedCharacterValues };
